import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { NbTabComponent } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AnalyticsService } from '../../@core/utils';
import { FILTER_OPTIONS, SIGNUP_TAB_TITLES } from '../../config/administator-role';
import { ROLEVALUES } from '../../enum/roles.enum';
import { SUBJECT_DISPLAY_NAMES } from '../../enum/subject-enum';
import { SignupActivityTabs, UserActivityStatus, UserStatus } from '../../enum/user-activity-status.enum';
import { AuthService } from '../../services/auth.service';
import { StorageService } from '../../services/storage.service';
import { SubjectService } from '../../services/subject.service';
import { UtilService } from '../../services/util.service';

@Component({
  selector: 'ngx-signup-activity',
  templateUrl: './signup-activity.component.html',
  styleUrl: './signup-activity.component.scss',
})
export class SignupActivityComponent implements OnInit, OnDestroy {
  currentPage = 1;
  totalPages = 1;
  pages: number[] = [];
  isPaginate = true;
  teacherUsers: any[] = [];
  eduPlatformUsers: any[] = [];
  allTeacherUsers: any[] = [];
  allEduPlatformUsers: any[] = [];
  teacherOnlyUserCount = 0;
  eduPlatformUserCount = 0;
  loading = false;
  childLoading = false;
  selectedTab: SignupActivityTabs = SignupActivityTabs.TEACHERS_ONLY;
  selectedOption = FILTER_OPTIONS.All;
  searchText = '';
  searchControl = new FormControl();
  suppressSearchTrigger = false;
  filterOptions = Object.keys(FILTER_OPTIONS).map((key) => ({
    name: key,
    value: FILTER_OPTIONS[key],
  }));
  currentSchoolId;
  teacherUsersOnline = [];
  eduPlatformUsersOnline = [];
  teacherUsersOffline = [];
  eduPlatformUsersOffline = [];
  singupSubscription: Subscription;
  activeTeachers: any[];
  inactiveTeachers: any[];

  constructor(
    private authService: AuthService,
    public route: Router,
    public storageService: StorageService,
    public alayticsService: AnalyticsService,
    public spinner: NgxSpinnerService,
    public subjectService: SubjectService,
    private utilService: UtilService,
  ) {}

  async ngOnInit() {
    this.singupSubscription = this.storageService.setSelectedSchoolsToLocalStorage.subscribe(async (res) => {
      if (res) {
        await this.getUserSignupActivityData();
        setTimeout(async () => {
          this.allTeacherUsers = [];
          this.allEduPlatformUsers = [];
          await this.getAllUsers();
        }, 2000);
      }
    });

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => this.searchTeacher(value)),
      )
      .subscribe();
    setTimeout(async () => await this.getAllUsers(), 2000);
  }

  onTabChange(event: NbTabComponent) {
    const title = event.tabTitle;

    this.selectedTab =
      title === SIGNUP_TAB_TITLES.TeachersOnly ? SignupActivityTabs.TEACHERS_ONLY : SignupActivityTabs.EDU_PLATFORM;

    this.currentPage = 1;
    this.selectedOption = FILTER_OPTIONS.All;
    this.isPaginate = true;
    this.searchControl.setValue('');

    this.suppressSearchTrigger = true;

    this.getUserSignupActivityData().then(() => {
      this.suppressSearchTrigger = false;
    });
  }

  // Get the count of the users.
  // async getAllSignupCounts() {
  //   try {
  //     const schoolId = this.getCurrentSchool();

  //     const [teacherRes, eduRes] = await Promise.all([
  //       this.authService.getSignupActivity({ page: 1, teachers_only: true }) as any,
  //       this.authService.getSignupActivity({ page: 1, teachers_only: false, school_id: schoolId }) as any,
  //     ]);

  //     this.teacherOnlyUserCount = teacherRes?.items?.[0]?.teacher_only_user_count || 0;
  //     this.eduPlatformUserCount = eduRes?.items?.[0]?.real_user_count_per_school_id || 0;
  //   } catch (err) {
  //     // console.error('Error fetching counts:', err);
  //   }
  // }

  //Get the page wise data of the users.
  async getUserSignupActivityData() {
    try {
      this.spinner.show();
      this.loading = true;
      const schoolId = this.getCurrentSchool();

      const teacherParams: any = {
        page: this.currentPage,
        teachers_only: true,
        sort_type: this.selectedOption,
      };

      const eduParams: any = {
        page: this.currentPage,
        school_id: schoolId,
        sort_type: this.selectedOption,
      };

      const [teacherRes, eduRes]: any = await Promise.all([
        this.authService.getSignupActivity(teacherParams),
        this.authService.getSignupActivity(eduParams),
      ]);

      this.totalPages = this.selectedTab === SignupActivityTabs.TEACHERS_ONLY ? teacherRes.pageTotal : eduRes.pageTotal;
      this.loading = false;
      this.spinner.hide();

      this.teacherUsers = teacherRes.items || [];
      this.teacherOnlyUserCount = this.teacherUsers[0]?.teacher_only_user_count || 0;

      this.eduPlatformUsers = eduRes.items || [];
      this.eduPlatformUserCount = this.eduPlatformUsers[0]?.real_user_count_per_school_id || 0;

      this.childLoading = true;
      await this.getSubjectForTeacherPerClassroom(this.teacherUsers);
      this.childLoading = false;

      const currentTime = Date.now();
      const inactiveTeachers = [];
      this.teacherUsers.forEach((teacher) => {
        const activityLog = teacher.last_user_activity_log_of_user?.created_at;
        if (!activityLog) return;

        const lastLoginTime = new Date(activityLog).getTime();
        const timeDifference = currentTime - lastLoginTime;
        const checkActiveHour = 24 * 60 * 60 * 1000;

        if (timeDifference > checkActiveHour) {
          teacher.user_activity_status = 'logged_out';
          inactiveTeachers.push(teacher);
        }
      });

      if (inactiveTeachers.length > 0) {
        await this.utilService.updateTeacherLoginStatus({ teachers_data: inactiveTeachers });
      }
    } catch (err) {
      console.error('Error fetching user signup activity:', err);
    } finally {
      this.spinner.hide();
      this.generatePageList();
    }
  }

  getCurrentSchool() {
    const selectedSchool = this.storageService.getItem('selectedSchoolByAdmin');
    this.currentSchoolId = selectedSchool?.id;
    return selectedSchool?.id;
  }

  getStatusImagePath(status: string): string {
    return status === UserActivityStatus.ACTIVE ? UserStatus.ACTIVE : UserStatus.INACTIVE;
  }
  async getListByPage() {
    await this.getUserSignupActivityData();
  }

  // Generate list of pages.
  generatePageList() {
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  private filterByStatus(users: any[], statusList: UserActivityStatus[]) {
    return users.filter((user) => statusList.includes(user.user_activity_status));
  }

  filterUsersByRole() {
    const selected = this.selectedOption;
    switch (selected) {
      case FILTER_OPTIONS.Online:
        this.isPaginate = false;
        this.teacherUsersOnline = this.filterByStatus(this.allTeacherUsers, [UserActivityStatus.ACTIVE]);
        this.eduPlatformUsersOnline = this.filterByStatus(this.allEduPlatformUsers, [UserActivityStatus.ACTIVE]);
        this.teacherUsers = [...this.teacherUsersOnline];
        this.eduPlatformUsers = [...this.eduPlatformUsersOnline];
        break;

      case FILTER_OPTIONS.Offline:
        this.isPaginate = false;
        this.teacherUsersOffline = this.filterByStatus(this.allTeacherUsers, [
          UserActivityStatus.INACTIVE,
          UserActivityStatus.LOGGED_OUT,
        ]);
        this.eduPlatformUsersOffline = this.filterByStatus(this.allEduPlatformUsers, [
          UserActivityStatus.INACTIVE,
          UserActivityStatus.LOGGED_OUT,
        ]);
        this.teacherUsers = [...this.teacherUsersOffline];
        this.eduPlatformUsers = [...this.eduPlatformUsersOffline];
        break;

      default:
        this.isPaginate = true;
        this.searchControl.setValue('');
        this.getUserSignupActivityData();
        break;
    }
  }

  async searchTeacher(value: string) {
    if (this.suppressSearchTrigger) return;

    this.loading = true;
    const searchValue = value.toLowerCase().trim();
    const selectedUsers =
      this.selectedTab === SignupActivityTabs.TEACHERS_ONLY ? this.allTeacherUsers : this.allEduPlatformUsers;

    const updateUsers = (filteredUsers: any[]) => {
      if (this.selectedTab === SignupActivityTabs.TEACHERS_ONLY) {
        this.teacherUsers = filteredUsers;
      } else {
        this.eduPlatformUsers = filteredUsers;
      }
    };

    try {
      if (searchValue) {
        this.isPaginate = false;
        const filteredUsers = selectedUsers.filter(
          (user) =>
            user.name?.toLowerCase().includes(searchValue) ||
            user.email?.toLowerCase().includes(searchValue) ||
            user.role?.name?.toLowerCase().includes(searchValue),
        );
        updateUsers(filteredUsers);
      } else {
        switch (this.selectedOption) {
          case FILTER_OPTIONS.Online:
            this.isPaginate = false;
            updateUsers(
              this.selectedTab === SignupActivityTabs.TEACHERS_ONLY
                ? this.teacherUsersOnline
                : this.eduPlatformUsersOnline,
            );
            break;

          case FILTER_OPTIONS.Offline:
            this.isPaginate = false;
            updateUsers(
              this.selectedTab === SignupActivityTabs.TEACHERS_ONLY
                ? this.teacherUsersOffline
                : this.eduPlatformUsersOffline,
            );
            break;

          default:
            this.isPaginate = true;
            await this.getUserSignupActivityData();
            break;
        }
      }
    } catch (error) {
      // console.error('Search error:', error);
    } finally {
      this.loading = false;
    }
  }

  // Get the all the uses without pagination.
  async getAllUsers() {
    try {
      const schoolId = this.getCurrentSchool();
      const [teacherRes, eduRes] = await Promise.all([
        this.authService.getSignupActivity({ page: 0, teachers_only: true }),
        this.authService.getSignupActivity({ page: 0, teachers_only: false, school_id: schoolId }),
      ]);

      this.allTeacherUsers.push(...Object.values(teacherRes || {}));
      this.allEduPlatformUsers.push(...Object.values(eduRes || {}));
      this.teacherOnlyUserCount = this.allTeacherUsers[0]?.teacher_only_user_count || 0;
      this.eduPlatformUserCount = this.allEduPlatformUsers[0]?.real_user_count_per_school_id || 0;

      await this.getSubjectForTeacherPerClassroom(this.allTeacherUsers);
    } catch (err) {
      // console.error('Error fetching all users:', err);
    }
  }
  async getSubjectForTeacherPerClassroom(teacherList: any): Promise<void> {
    for (const teacher of teacherList) {
      let count: number = 0;
      let golbalPlansCount = 0;
      let manualPlansCount = 0;
      const subjects = await this.getSubjectByIds(teacher);
      teacher.teachingSubjectName = subjects;
      if (teacher?.classroom_list?.length > 0) {
        for (const classroom of teacher.classroom_list) {
          if (classroom?.isManual) {
            count = classroom.total_added_manual_pacing_plans > 0 ? count + 1 : 0;
            manualPlansCount = manualPlansCount + classroom.total_added_manual_pacing_plans;
          } else {
            if (classroom.global_pacing_plan.some((subArray) => subArray.length > 0)) {
              golbalPlansCount = golbalPlansCount + classroom.global_pacing_plan[0].length;
              count += 1;
            }
          }
        }
      }
      teacher.totalLessonPlans = golbalPlansCount + manualPlansCount;
      teacher.countClassWithPlans = count;
    }
  }

  async getSubjectByIds(user) {
    try {
      // Made the teacher_info optional chaining to avoid error
      const subject_ids = user?.teachingInfo?.teachingSubject;
      if (subject_ids && subject_ids.length > 0) {
        return subject_ids
          .map((id) => {
            return SUBJECT_DISPLAY_NAMES[id];
          })
          .filter((subject) => subject !== undefined)
          .map((subject) => subject.toString())
          .join(', ');
      } else return null;
    } catch (error) {
      console.error('Error getting subject by IDs:', error);
    }
  }
  showTeacherDashboard(user): void {
    try {
      switch (user.role_id) {
        case ROLEVALUES.TEACHER_ONLY:
          this.route.navigateByUrl(`/profile/teacher-details/${user.id}/true`, {
            state: { superadminSignupActivity: true },
          });
          break;
        case ROLEVALUES.SUPPORT_STAFF:
        case ROLEVALUES.SPED_COORDINATOR:
        case ROLEVALUES.VICE_PRINCIPAL:
        case ROLEVALUES.PRINCIPAL:
        case ROLEVALUES.EXECUTIVE_DIRECTOR:
        case ROLEVALUES.COO:
        case ROLEVALUES.CBO:
        case ROLEVALUES.CAO:
        case ROLEVALUES.IT_ADMINISTRATOR:
          this.route.navigateByUrl(`/profile/users/${user.id}`);
          break;
        case ROLEVALUES.TEACHER_ASSISTANT:
        case ROLEVALUES.RSP_TEACHER:
        case ROLEVALUES.TEACHER:
        case ROLEVALUES.SPED_SUPPORT_STAFF:
          this.route.navigateByUrl(`/profile/teacher-details/${user.id}/true`, {
            state: { superadminSignupActivity: true, currentSchoolId: this.currentSchoolId },
          });

          break;
        case ROLEVALUES.PARENT:
        case ROLEVALUES.STUDENT:
        case ROLEVALUES.CAFETERIA_STAFF:
        case ROLEVALUES.MAINTENANCE:
        case ROLEVALUES.OFFICE_STAFF:
        case ROLEVALUES.OFFICE_MANAGER:
        case ROLEVALUES.SPEECH_THERAPIST:
          this.route.navigateByUrl(`/profile/users/${user.id}`);
          break;
      }
      // }
    } catch (error) {
      console.log(error);
    }
  }

  ngOnDestroy() {
    this.singupSubscription?.unsubscribe();
  }
}
